import React, { useState, useEffect } from "react";
import { Formik } from "formik";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Form, InputGroup, Button, Container, Row, Col } from "react-bootstrap";
import { AiOutlineExclamationCircle } from "react-icons/ai";
import RequiredFieldMessage from "../../common/userMessages/requiredFieldMessage";
import userService from "../../../services/userService";
import {
  getValidationSchema,
  getDays,
  getTimeSlots,
  getLanguages,
} from "../formEntities/generalEntities";
import SaveAndContinueUserMessage from "../../common/userMessages/saveAndContinueMessage";

const GeneralForm = ({ shake, user, updatePage, nextLink, userData,  pullUserData }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showReqFieldMessage, setShowReqFieldMessage] = useState(false);

  const validationSchema = getValidationSchema(t);
  const dayAvailabilityArr = getDays(t);
  const timeAvailabilityArr = getTimeSlots(t);
  const languagesArr = getLanguages(t);

  useEffect(() => {
    if(!userData.agencyInfo) 
      pullUserData();
  }, []);

  // Convert MM/DD/YYYY to YYYY-MM-DD for date input
  const toDateInputFormat = (dateStr) => {
    if (!dateStr || dateStr.length !== 10) return "";
    const [month, day, year] = dateStr.split("/");
    return `${year}-${month}-${day}`;
  };

  // Convert YYYY-MM-DD to MM/DD/YYYY for storage
  const toStorageFormat = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${month}/${day}/${year}`;
  };

  const styles = {
    errorStyle: {
      border: "2px solid red",
      borderTopRightRadius: "0",
      borderBottomRightRadius: "0",
    },
    fieldErrMessage: {
      color: "red",
      paddingLeft: "5px",
    },
    errIcon: {
      fontSize: "20px",
      color: "red",
    },
    inputGroupTextStyle: {
      border: "1px solid red",
      background: "white",
    },
    errText: {
      color: "red",
    },
  };

  const handleValidations = (values) => {
    const errors = {};
    if (values.daysAvailable.length === 0) {
      errors.daysAvailable = t("common.required");
    }

    if (values.timesAvailable.length === 0) {
      errors.timesAvailable = t("common.required");
    }

    if (
      values.startDate !== "" &&
      !/^(0?[1-9]|1[0-2])[\/](0?[1-9]|[1-2][0-9]|3[01])[\/]\d{4}$/i.test(
        values.startDate
      )
    ) {
      errors.startDate = t("general.validation.invalidDateFormat");
    }

    if (
      values.office &&
      values.poi &&
      values.daysAvailable &&
      values.timesAvailable &&
      values.validLicense &&
      values.carAndIns &&
      Object.keys(errors).length === 0
    ) {
      setShowReqFieldMessage(false);
    }
    return errors;
  };

  const handleSaveAndContinue = (values, errors) => {
    if (
      !values.gender ||
      !values.office ||
      !values.poi ||
      !values.daysAvailable ||
      !values.timesAvailable ||
      !values.validLicense ||
      !values.carAndIns ||
      Object.keys(errors).length >= 1
    ) {
      shake();
      setShowReqFieldMessage(true);
    }
  };

  const handleFormikSubmit = async (values) => {
    Object.keys(values).forEach((key) => {
      if (values[key] === "" || !values[key]) {
        delete values[key];
      }
      if (key === "office") {
        values[key] = values[key].substr(values[key].length - 3)
      }
      if (key === "poi") {
        if (values[key].indexOf("PCA") > -1 || values[key].indexOf("CNA") > -1)
          values[key] = values[key].substr(values[key].length - 3)
        else if (values[key].indexOf("Nurse") > -1)
          values[key] = values[key].substr(values[key].length - 2)
        else if (values[key].indexOf("Admin") > -1)
          values[key] = "Admin";
      }
    });
    await userService.saveUserData(user, "general", values);
    userData.general = values;
    updatePage();
    navigate(`../${nextLink}`);
  };

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={{
        gender: userData.general ? userData.general.gender ? userData.general.gender : "" : "",
        office: userData.general ? userData.general.office ? userData.general.office : "" : "",
        poi: userData.general ? userData.general.poi ? userData.general.poi : "" : "",
        daysAvailable: userData.general ? userData.general.daysAvailable ? userData.general.daysAvailable : [] : [],
        timesAvailable: userData.general ? userData.general.timesAvailable ? userData.general.timesAvailable : [] : [],
        startDate: userData.general ? userData.general.startDate ? userData.general.startDate : "" : "",
        validLicense: userData.general ? userData.general.validLicense ? userData.general.validLicense : "" : "",
        carAndIns: userData.general ? userData.general.carAndIns ? userData.general.carAndIns : "" : "",
        languages: userData.general ? userData.general.languages ? userData.general.languages : [] : [],
        skills: userData.general ? userData.general.skills ? userData.general.skills : "" : "",
      }}
      validate={(values) => handleValidations(values)}
      onSubmit={(values) => handleFormikSubmit(values)}
    >
      {({ values, errors, touched, handleChange, handleSubmit, setFieldValue, setValues, setValue }) => (
        <Container>
          {/* {console.log("Errors:", errors)} */}
          <Form noValidate onSubmit={handleSubmit}>
            {/* Gender */}
            <section>
              <div className="d-flex align-items-center">
                <h3 className="me-2">{t("general.gender")}</h3>
                <em>{t("common.required")} *</em>
              </div>
              <Row>
                <Col sm={12} md={6} className="mb-3 text-start">
                  <Form.Check
                    inline
                    type="radio"
                    id="gender1"
                    label={t("general.male")}
                    name="gender"
                    value="2"
                    onChange={handleChange}
                    disabled={userData.submissionDate}
                    checked={values.gender === "2" ? true : null}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    id="gender2"
                    label={t("general.female")}
                    name="gender"
                    value="1"
                    onChange={handleChange}
                    disabled={userData.submissionDate}
                    checked={values.gender === "1" ? true : null}
                  />
                </Col>
              </Row>
            </section>
            {/* Resident 5 years */}
            <section>
              <div className="d-flex align-items-center">
                <h3 className="me-2">{t("general.residentNC")}</h3>
                <em>{t("common.required")} *</em>
              </div>
              <Row>
                <Col sm={12} md={6} className="mb-3 text-start">
                  <Form.Check
                    inline
                    type="radio"
                    id="res1"
                    label={t("common.yes")}
                    name="resNC"
                    value="Y"
                    onChange={handleChange}
                    disabled={userData.submissionDate}
                    checked={values.residencyNC === "Y" ? true : null}
                  />

                  <Form.Check
                    inline
                    type="radio"
                    id="res2"
                    label={t("common.no")}
                    name="resNC"
                    value="N"
                    onChange={handleChange}
                    disabled={userData.submissionDate}
                    checked={values.residencyNC === "N" ? true : null}
                  />
                </Col>
              </Row>
            </section>
            {/* Offices */}
            <section>
              <div className="d-flex align-items-center">
                <h3 className="me-2">{t("general.officeOfInterest")}</h3>
                <em>{t("common.required")} *</em>
              </div>
              <p
                className="text-start"
                style={errors.office && touched.office && styles.errText}
              >
                {t("general.selectOffice")}
              </p>
              {userData.agencyInfo ? (userData.agencyInfo.offices.map((office, index) => (
                <div className="text-start mb-0" key={office.name}>
                  <Form.Check
                    type="radio"
                    id={office.name}
                    label={office.name}
                    name="office"
                    value={office.name}
                    onChange={handleChange}
                    disabled={userData.submissionDate}
                    checked={office.name === values.office ? true : null}
                  />

                  <ul className="office-cities">
                    {office.locationOffices.map((officeCity) => (
                      <li key={officeCity}>{officeCity}</li>
                    ))}
                  </ul>
                </div>
              ))) : (
                <h4 className = "text-danger">{t("general.pleaseRefresh")}</h4>
              )}
            </section>
            <hr />

            {/* Position of Interest  */}
            <section>
              <div className="d-flex align-items-center">
                <h3 className="me-2">{t("general.positionOfInterest")}</h3>
                <em>{t("common.required")} *</em>
              </div>
              <p
                className="text-start"
                style={errors.poi && touched.poi && styles.errText}
              >
                {t("general.selectPosition")}
              </p>

              <div className="text-start">
                {userData.agencyInfo ? (userData.agencyInfo.poi.map((p, index) => (
                  <Form.Check
                    type="radio"
                    id={p}
                    label={p}
                    key={p}
                    className="mb-3 w-100"
                    name="poi"
                    value={p}
                    onChange={handleChange}
                    disabled={userData.submissionDate}
                    checked={p === values.poi ? true : null}
                  />
                ))) : (
                  <h4 className = "text-danger">{t("general.pleaseRefresh")}</h4>
                )}
              </div>
            </section>
            <hr />
            {/* Availability  */}
            <section>
              <h3>{t("general.availability")}</h3>
              <Row>
                <Col sm={12} md={6} className="text-start mb-3">
                  <Form.Group>
                    <Form.Label
                      style={
                        errors.daysAvailable &&
                        touched.daysAvailable &&
                        styles.errText
                      }
                    >
                      <strong>{t("general.availableDays")}</strong>
                      <br />
                      {t("general.selectDays")}
                    </Form.Label>
                    {dayAvailabilityArr.map((day, i) => (
                      <Form.Check
                        key={day.value}
                        type="checkbox"
                        label={day.label}
                        id={day.value}
                        value={day.value}
                        name="daysAvailable"
                        onChange={handleChange}
                        disabled={userData.submissionDate}
                        checked={values.daysAvailable.indexOf(day.value) > -1}
                      />
                    ))}
                  </Form.Group>
                </Col>
                <Col sm={12} md={6} className="text-start mb-3">
                  <Form.Group required>
                    <Form.Label
                      style={
                        errors.timesAvailable &&
                        touched.timesAvailable &&
                        styles.errText
                      }
                    >
                      <strong>{t("general.availabilityHours")}</strong>
                      <br />
                      {t("general.selectTimeSlots")}
                    </Form.Label>
                    {timeAvailabilityArr.map((time, i) => (
                      <Form.Check
                        key={time.value}
                        type="checkbox"
                        label={time.label}
                        id={time.value}
                        value={time.value}
                        name="timesAvailable"
                        onChange={handleChange}
                        disabled={userData.submissionDate}
                        checked={values.timesAvailable.indexOf(time.value) > -1}
                      />
                    ))}
                  </Form.Group>
                </Col>
                <Col sm={12} md={6} className="text-start mb-3">
                  <Form.Group className="mb-3" controlId="start-date">
                    <Form.Label>{t("general.startDate")}</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="date"
                        name="startDate"
                        required={true}
                        onChange={(e) => setFieldValue("startDate", toStorageFormat(e.target.value))}
                        value={toDateInputFormat(values.startDate)}
                        style={
                          errors.startDate && touched.startDate
                            ? styles.errorStyle
                            : null
                        }
                        disabled={userData.submissionDate}
                      />
                      {errors.startDate && touched.startDate && (
                        <InputGroup.Text style={styles.inputGroupTextStyle}>
                          <AiOutlineExclamationCircle style={styles.errIcon} />
                        </InputGroup.Text>
                      )}
                    </InputGroup>
                    {errors.startDate && touched.startDate ? (
                      <div style={styles.fieldErrMessage}>
                        {errors.startDate}
                      </div>
                    ) : null}
                  </Form.Group>
                </Col>
              </Row>
            </section>
            <hr />

            {/* Personal Transport  */}
            <section>
              <div className="d-flex align-items-center">
                <h3 className="me-2">{t("general.personalTransportation")}</h3>
                <em>{t("common.required")} *</em>
              </div>
              <p className="text-start">
                {t("general.answerBothTransport")}
              </p>
              <Row>
                <Col sm={12} md={6} className="mb-3 text-start">
                  <p
                    style={{
                      color:
                        errors.validLicense && touched.validLicense && "red",
                    }}
                  >
                    {t("general.validLicenseQuestion")}
                  </p>
                  <Form.Check
                    inline
                    type="radio"
                    label={t("common.yes")}
                    id="inline-radio-1"
                    name="validLicense"
                    value="Yes"
                    onChange={handleChange}
                    disabled={userData.submissionDate}
                    checked={values.validLicense === "Yes" ? true : null}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    label={t("common.no")}
                    id="inline-radio-2"
                    name="validLicense"
                    value="No"
                    onChange={handleChange}
                    disabled={userData.submissionDate}
                    checked={values.validLicense === "No" ? true : null}
                  />
                </Col>
                <Col sm={12} md={6} className="mb-3 text-start">
                  <p
                    style={{
                      color: errors.carAndIns && touched.carAndIns && "red",
                    }}
                  >
                    {t("general.carAndInsuranceQuestion")}
                  </p>
                  <Form.Check
                    inline
                    type="radio"
                    label={t("common.yes")}
                    id="inline-radio-3"
                    name="carAndIns"
                    value="Yes"
                    onChange={handleChange}
                    disabled={userData.submissionDate}
                    checked={values.carAndIns === "Yes" ? true : null}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    label={t("common.no")}
                    id="inline-radio-4"
                    name="carAndIns"
                    value="No"
                    onChange={handleChange}
                    disabled={userData.submissionDate}
                    checked={values.carAndIns === "No" ? true : null}
                  />
                </Col>
              </Row>
            </section>
            <hr />

            {/* Languages/Skills  */}
            <section>
              <Row>
                <h3>{t("general.languagesSkills")}</h3>
                <Col sm={12} md={6} className="text-start mb-3">
                  <Form.Group>
                    <Form.Label>{t("general.selectAllLanguages")}</Form.Label>
                    {languagesArr.map((language, i) => (
                      <Form.Check
                        key={language.value}
                        type="checkbox"
                        label={language.label}
                        id={language.value}
                        value={language.value}
                        name="languages"
                        onChange={handleChange}
                        disabled={userData.submissionDate}
                        checked={values.languages.indexOf(language.value) > -1}
                      />
                    ))}
                  </Form.Group>
                </Col>
                <Col sm={12} md={6} className="text-start">
                  <Form.Group className="mb-3" controlId="skills">
                    <Form.Label>{t("general.listAdditionalSkills")}</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="skills"
                      onChange={handleChange}
                      disabled={userData.submissionDate}
                      value={values.skills}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </section>

            {/* Submit Section  */}
            <section>
              <RequiredFieldMessage
                handleClick={() => navigate(`../${nextLink}`)}
                show={showReqFieldMessage ? true : false}
                reqMessage={t("general.completeRequiredFields")}
                hasButton={true}
              />
              <SaveAndContinueUserMessage messageType="save and continue" />
              <Button
                variant="primary"
                type="submit"
                onClick={() => handleSaveAndContinue(values, errors)}
              >
                {t("common.saveAndContinue")}
              </Button>
            </section>
          </Form>
        </Container>
      )}
    </Formik>
  );
};

export default GeneralForm;
